import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { 
  Calendar, 
  FileText, 
  Tag, 
  User, 
  Download,
  Check,
  X,
  AlertTriangle,
  Clock
} from 'lucide-react';
import ExpenseStatusBadge from '../contractor/ExpenseStatusBadge';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  EXPENSE_CATEGORY_INFO,
  ExpenseApprovalData
} from '@/types/expense';

interface ExpenseApprovalModalProps {
  expense: Expense | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (expenseId: string, data: ExpenseApprovalData) => Promise<void>;
  onReject: (expenseId: string, data: ExpenseApprovalData) => Promise<void>;
}

const ExpenseApprovalModal: React.FC<ExpenseApprovalModalProps> = ({
  expense,
  isOpen,
  onClose,
  onApprove,
  onReject
}) => {
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const { toast } = useToast();

  // Reset form when expense changes
  useEffect(() => {
    if (expense) {
      setNotes('');
      setRejectionReason('');
      setShowRejectionForm(false);
    }
  }, [expense]);

  const handleApprove = async () => {
    if (!expense) return;

    try {
      setIsSubmitting(true);
      await onApprove(expense.id, {
        approved: true,
        notes: notes.trim() || undefined
      });
      onClose();
    } catch (error) {
      console.error('Error approving expense:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!expense) return;

    if (!rejectionReason.trim()) {
      toast({
        title: 'Rejection reason required',
        description: 'Please provide a reason for rejecting this expense',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await onReject(expense.id, {
        approved: false,
        rejectionReason: rejectionReason.trim(),
        notes: notes.trim() || undefined
      });
      onClose();
    } catch (error) {
      console.error('Error rejecting expense:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewReceipt = async () => {
    if (!expense) return;
    
    try {
      await expenseService.downloadReceipt(expense.id);
    } catch (error: any) {
      console.error('Error viewing receipt:', error);
      toast({
        title: 'Error',
        description: 'Failed to view receipt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatAmount = (amount: string) => {
    return expenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string) => {
    return expenseService.formatDate(dateString);
  };

  const getCategoryInfo = (category: ExpenseCategory) => {
    return EXPENSE_CATEGORY_INFO[category];
  };

  if (!expense) return null;

  const categoryInfo = getCategoryInfo(expense.category);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Expense</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Expense Overview */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ExpenseStatusBadge status={expense.status} size="lg" />
              <Badge variant="outline" className="text-yellow-600 border-yellow-200">
                Pending Review
              </Badge>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{formatAmount(expense.amount)}</div>
              <div className="text-sm text-muted-foreground">Total Amount</div>
            </div>
          </div>

          <Separator />

          {/* Expense Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Expense Details</h3>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-muted-foreground">Description</Label>
                  <div className="mt-1 p-3 bg-muted/50 rounded-md">
                    {expense.description}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Expense Date
                    </Label>
                    <div className="mt-1 font-medium">{formatDate(expense.expenseDate)}</div>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Category
                    </Label>
                    <div className="mt-1 flex items-center gap-2">
                      <span>{categoryInfo.icon}</span>
                      <span className="font-medium">{categoryInfo.label}</span>
                    </div>
                  </div>
                </div>

                {expense.notes && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Notes</Label>
                    <div className="mt-1 p-3 bg-muted/50 rounded-md text-sm">
                      {expense.notes}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submission Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Submission Info</h3>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-muted-foreground flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Submitted By
                  </Label>
                  <div className="mt-1 font-medium">
                    {expense.submittedByEmail || 'Unknown'}
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Submitted At
                  </Label>
                  <div className="mt-1 font-medium">
                    {expense.submittedAt ? formatDate(expense.submittedAt) : 'Unknown'}
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">Expense ID</Label>
                  <div className="mt-1 text-sm font-mono bg-muted/50 p-2 rounded">
                    {expense.id}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Receipt Section */}
          {(expense.receiptUrl || expense.receiptFilename) && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Receipt</h3>
                
                <div className="bg-muted/50 p-4 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      {expense.receiptFilename && (
                        <div className="font-medium">{expense.receiptFilename}</div>
                      )}
                      <div className="text-sm text-muted-foreground">
                        Receipt attached to this expense
                      </div>
                    </div>
                    {expense.receiptUrl && (
                      <Button
                        variant="outline"
                        onClick={handleViewReceipt}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        View Receipt
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Review Section */}
          <Separator />
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Your Review</h3>
            
                         {!showRejectionForm ? (
               <div className="space-y-4">
                 {/* Optional Notes */}
                 <div>
                   <Label htmlFor="notes" className="text-sm font-medium">
                     Comments (Optional)
                   </Label>
                   <Textarea
                     id="notes"
                     placeholder="Add any comments about this expense..."
                     value={notes}
                     onChange={(e) => setNotes(e.target.value)}
                     className="mt-1"
                     rows={3}
                     maxLength={500}
                   />
                   <div className="text-xs text-muted-foreground mt-1">
                     {notes.length}/500 characters
                   </div>
                 </div>

                 {/* Action Buttons */}
                 <div className="flex gap-3">
                   <Button
                     onClick={handleApprove}
                     disabled={isSubmitting}
                     className="flex-1 gap-2"
                   >
                     <Check className="h-4 w-4" />
                     {isSubmitting ? 'Approving...' : 'Approve Expense'}
                   </Button>
                   
                   <Button
                     variant="outline"
                     onClick={() => setShowRejectionForm(true)}
                     disabled={isSubmitting}
                     className="flex-1 gap-2 text-red-600 border-red-200 hover:bg-red-50"
                   >
                     <X className="h-4 w-4" />
                     Reject Expense
                   </Button>
                 </div>
               </div>
             ) : (
               <div className="space-y-4">
                 {/* Rejection Form */}
                 <div className="bg-red-50 border border-red-200 p-4 rounded-md">
                   <div className="flex items-center gap-2 mb-3">
                     <AlertTriangle className="h-5 w-5 text-red-600" />
                     <h4 className="font-medium text-red-800">Reject Expense</h4>
                   </div>
                   
                   <div className="space-y-3">
                     <div>
                       <Label htmlFor="rejectionReason" className="text-sm font-medium text-red-800">
                         Reason for Rejection *
                       </Label>
                       <Textarea
                         id="rejectionReason"
                         placeholder="Please explain why this expense is being rejected..."
                         value={rejectionReason}
                         onChange={(e) => setRejectionReason(e.target.value)}
                         className="mt-1 border-red-200 focus:border-red-300"
                         rows={3}
                         maxLength={500}
                         required
                       />
                       <div className="text-xs text-red-600 mt-1">
                         {rejectionReason.length}/500 characters
                       </div>
                     </div>

                     <div>
                       <Label htmlFor="rejectionNotes" className="text-sm font-medium text-red-800">
                         Additional Comments (Optional)
                       </Label>
                       <Textarea
                         id="rejectionNotes"
                         placeholder="Any additional feedback for the contractor..."
                         value={notes}
                         onChange={(e) => setNotes(e.target.value)}
                         className="mt-1 border-red-200 focus:border-red-300"
                         rows={2}
                         maxLength={500}
                       />
                     </div>
                   </div>
                 </div>

                 {/* Rejection Action Buttons */}
                 <div className="flex gap-3">
                   <Button
                     variant="outline"
                     onClick={() => setShowRejectionForm(false)}
                     disabled={isSubmitting}
                     className="flex-1"
                   >
                     Cancel
                   </Button>
                   
                   <Button
                     onClick={handleReject}
                     disabled={isSubmitting || !rejectionReason.trim()}
                     variant="destructive"
                     className="flex-1 gap-2"
                   >
                     <X className="h-4 w-4" />
                     {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                   </Button>
                 </div>
               </div>
             )}
          </div>

          {/* Guidelines */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
            <h4 className="font-medium text-blue-800 mb-2">Review Guidelines</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Check that the expense is business-related and reasonable</li>
              <li>• Verify the receipt matches the claimed amount</li>
              <li>• Ensure the expense date is within the contract period</li>
              <li>• Review the category and description for accuracy</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          <div className="text-sm text-muted-foreground">
            Expense requires your approval to proceed
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseApprovalModal; 